import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ListChecks, SlidersHorizontal, Trash2 } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize, money, newIdempotencyKey } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { BatchSearch, StoreSelect, formatQty, invalidateStockControl, parseQty, productTitle, signedMoney, useStoreOptions } from './stock-control-common';
import { LOSS_CATEGORIES, type AdjustmentOutcome, type StockBatchHit } from './stock-control-types';

const APPROVALS_TAB = '/stock-control?tab=approvals';

type AdjustmentKind = 'adjustment' | 'write_off';

interface DraftLine {
  batch: StockBatchHit;
  qty: string;
  reason: string;
  lossCategory: string;
}

function deltaFor(kind: AdjustmentKind, qtyText: string): number | null {
  const qty = parseQty(qtyText);
  if (qty === null || qty === 0) return null;
  if (kind === 'write_off') return qty > 0 ? -qty : null;
  return qty;
}

function reasonIsValid(reason: string): boolean {
  return reason.trim().length >= 3;
}

export function AdjustStockPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), APPROVALS_TAB);
  const stores = useStoreOptions();
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const [storeId, setStoreId] = useState('');
  const [kind, setKind] = useState<AdjustmentKind>('adjustment');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [error, setError] = useState<string | null>(null);

  const isWriteOff = kind === 'write_off';

  const addLine = (batch: StockBatchHit) => {
    setLines((current) => (current.some((line) => line.batch.id === batch.id) ? current : [...current, { batch, qty: '', reason: '', lossCategory: 'expired' }]));
  };

  const updateLine = (index: number, patch: Partial<DraftLine>) => {
    setLines((current) => current.map((line, position) => (position === index ? { ...line, ...patch } : line)));
  };

  const submit = useMutation({
    mutationFn: () =>
      post<AdjustmentOutcome>(
        '/pharmacy/stock/adjust',
        {
          storeId,
          type: kind,
          lines: lines.map((line) => ({
            stockBatchId: line.batch.id,
            qtyDelta: deltaFor(kind, line.qty),
            reason: line.reason.trim(),
            ...(isWriteOff ? { lossCategory: line.lossCategory } : {}),
          })),
        },
        { idempotencyKey },
      ),
    onSuccess: (result) => {
      if (result.status === 'applied') {
        toast.success(tr('pharmacy', 'stock_adjustment_applied'), `${result.adjusted} line${result.adjusted === 1 ? '' : 's'} changed · net ${signedMoney(result.netValueMinor)} at cost`);
      } else {
        toast.warning(`Sent for approval as ${result.number ?? 'a new request'}`, tr('pharmacy', 'worth_x_at_cost_the_stock_changes_once_someone', { valueMinor: money(result.valueMinor) }));
      }
      void invalidateStockControl(queryClient);
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const missing: string[] = [];
  if (!storeId) missing.push('a store');
  if (lines.length === 0) missing.push('at least one batch');
  if (lines.some((line) => deltaFor(kind, line.qty) === null)) {
    missing.push(isWriteOff ? 'a quantity above zero on every line' : 'a quantity other than zero on every line');
  }
  if (lines.some((line) => !reasonIsValid(line.reason))) missing.push('a reason of 3 or more characters on every line');

  const netCostMinor = lines.reduce((total, line) => {
    const delta = deltaFor(kind, line.qty);
    return delta === null ? total : total + Math.round(delta * line.batch.purchasePriceMinor);
  }, 0);

  const dirty = !submit.isSuccess && (storeId !== '' || kind !== 'adjustment' || lines.length > 0);

  return (
    <FormScreen
      title={tr('pharmacy', 'new_stock_adjustment')}
      description={tr('pharmacy', 'correct_a_count_or_write_off_lost_stock_above')}
      eyebrow={tr('pharmacy', 'stock_control')}
      icon={SlidersHorizontal}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (missing.length > 0) return;
        setError(null);
        submit.mutate();
      }}
    >
      <FormSection title={tr('opd', 'details')} icon={SlidersHorizontal}>
        <Field label={tr('pharmacy', 'store')} required hint={lines.length > 0 ? tr('pharmacy', 'remove_every_line_to_change_the_store') : undefined}>
          <StoreSelect value={storeId} onChange={setStoreId} stores={stores.data} emptyLabel={tr('pharmacy', 'select')} disabled={lines.length > 0} />
        </Field>
        <Field
          label={tr('pharmacy', 'type')}
          required
          hint={isWriteOff ? tr('pharmacy', 'stock_lost_or_destroyed_enter_the_quantity_as_a') : tr('pharmacy', 'correct_a_quantity_use_a_plus_sign_to_add_stock')}
        >
          <ChoiceButtons
            layout="cards"
            value={kind}
            onChange={setKind}
            options={[
              { value: 'adjustment', label: tr('pharmacy', 'adjustment_correct_a_quantity') },
              { value: 'write_off', label: tr('pharmacy', 'write_off_lost_damaged_expired') },
            ]}
          />
        </Field>
      </FormSection>

      <FormSection title={tr('pharmacy', 'batches')} icon={ListChecks} columns={1}>
        <BatchSearch
          storeId={storeId}
          disabled={!storeId}
          disabledHint="Choose a store to search its batches."
          isPicked={(batch) => lines.some((line) => line.batch.id === batch.id)}
          onPick={addLine}
        />

        {lines.length === 0 ? (
          <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">{tr('pharmacy', 'no_batches_added_yet_search_above_and_choose')}</p>
        ) : (
          <div className="space-y-2">
            {lines.map((line, index) => {
              const delta = deltaFor(kind, line.qty);
              const overdrawn = delta !== null && delta < 0 && Math.abs(delta) > line.batch.qtyOnHand;
              return (
                <div key={line.batch.id} className="rounded-md border p-2">
                  <div className="grid gap-2 sm:grid-cols-12">
                    <div className="sm:col-span-4">
                      <p className="text-sm font-medium">{productTitle(line.batch.product)}</p>
                      <p className="text-xs text-muted-foreground">
                        {tr('pharmacy', 'batch')} <span className="font-mono">{line.batch.batchNo}</span> · {formatQty(line.batch.qtyOnHand)} {line.batch.product.unit}{' '}
                        {tr('pharmacy', 'on_hand_cost')} {money(line.batch.purchasePriceMinor)}
                      </p>
                    </div>
                    <Input
                      className="tabular sm:col-span-2"
                      type="number"
                      step="any"
                      min={isWriteOff ? 0 : undefined}
                      aria-label={tr('pharmacy', 'quantity_for_x', { batchNo: line.batch.batchNo })}
                      placeholder={isWriteOff ? tr('pharmacy', 'qty_lost') : tr('pharmacy', 'qty')}
                      value={line.qty}
                      onChange={(event) => updateLine(index, { qty: event.target.value })}
                    />
                    <Input
                      className="sm:col-span-5"
                      aria-label={tr('pharmacy', 'reason_for_x', { batchNo: line.batch.batchNo })}
                      placeholder={tr('pharmacy', 'reason_required')}
                      maxLength={240}
                      value={line.reason}
                      onChange={(event) => updateLine(index, { reason: event.target.value })}
                    />
                    <Button
                      type="button"
                      className="sm:col-span-1"
                      variant="ghost"
                      size="icon"
                      aria-label={tr('pharmacy', 'remove_x', { batchNo: line.batch.batchNo })}
                      onClick={() => setLines((current) => current.filter((_, position) => position !== index))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {isWriteOff && (
                    <ChoiceButtons
                      className="mt-2"
                      layout="chips"
                      size="sm"
                      aria-label={tr('pharmacy', 'loss_category_for_x', { batchNo: line.batch.batchNo })}
                      value={line.lossCategory}
                      onChange={(lossCategory) => updateLine(index, { lossCategory })}
                      options={LOSS_CATEGORIES.map((category) => ({ value: category, label: humanize(category, 'loss_category') }))}
                    />
                  )}
                  {overdrawn && (
                    <p className="mt-1 text-xs text-warning">
                      {tr('pharmacy', 'that_is_more_than_the')} {formatQty(line.batch.qtyOnHand)} {tr('pharmacy', 'shown_on_hand_when_you_searched_the_server')}
                    </p>
                  )}
                </div>
              );
            })}
            <p className="text-end text-sm text-muted-foreground">
              {tr('pharmacy', 'net_effect_at_cost')} <strong className={netCostMinor < 0 ? 'text-destructive' : ''}>{signedMoney(netCostMinor)}</strong>
            </p>
          </div>
        )}
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={isWriteOff ? tr('pharmacy', 'submit_write_off') : tr('pharmacy', 'submit_adjustment')}
        loading={submit.isPending}
        incomplete={missing.length > 0}
        cancelTo={returnTo}
        note={missing.length > 0 ? `${tr('pharmacy', 'still_needed')} ${missing.join(', ')}` : undefined}
      />
    </FormScreen>
  );
}
